import { useMemo, useCallback, useRef } from "react";
import { useFormV3 } from "../context/FormV3Context/FormV3Context";

const getInitialFromField = (field: any) => {
   const value = field?.value;
   if (value && typeof value === "object") {
      return value.decoratedValue || value.originalValue || value;
   }
   return value ?? "";
};

export const useFieldController = ({
   field,
   formGUID,
   controlledValue,
   onControlledChange,
   onValueChange,
}: {
   field: any;
   formGUID: string;
   controlledValue?: any;
   onControlledChange?: (value: any) => void;
   onValueChange?: (args: { field: any; value: any; formGUID: string }) => void;
}) => {
   const isControlled = controlledValue !== undefined;
   const { formV3Data, changeFieldValue, fieldsInfo } = useFormV3();

   const valueFromContext = formV3Data?.[formGUID]?.[field?.id];

   // Cache initial value from field to avoid recalculating when field object reference changes
   // but content stays the same. Only reset when field.id changes.
   const initialValueRef = useRef<{ fieldId: string; value: any } | null>(null);
   if (!initialValueRef.current || initialValueRef.current.fieldId !== field?.id) {
      initialValueRef.current = {
         fieldId: field?.id,
         value: getInitialFromField(field),
      };
   }

   const fieldInfo = useMemo(() => {
      return fieldsInfo?.[formGUID]?.[field.id];
   }, [fieldsInfo, formGUID, field?.id]);

   const value = useMemo(() => {
      if (isControlled) return controlledValue;
      if (valueFromContext !== undefined) return valueFromContext;

      return initialValueRef.current?.value ?? "";
   }, [isControlled, controlledValue, valueFromContext]);

   const setValue = useCallback(
      (next: any) => {
         if (isControlled) {
            onControlledChange?.(next);
         } else {
            changeFieldValue(formGUID, field.id, next);
         }
         onValueChange?.({ field, value: next, formGUID });
      },
      [isControlled, onControlledChange, changeFieldValue, formGUID, field?.id, onValueChange],
   );

   return { isControlled, value, setValue, fieldInfo };
};
